// Raw IndexedDB on the main thread. Version and stores must mirror AssistantOrchestrator's
// `indexDBConfig` exactly; do not bump DB_VERSION. See .claude/context/persistence.md.

const DB_NAME = 'assistantBotDB';
const DB_VERSION = 2;

export const STORES = {
  MESSAGES: 'messages',
  SESSIONS: 'sessions',
};

// One cached connection, dropped whenever it stops being usable.
// See .claude/context/persistence.md ("Connection lifecycle").
let dbPromise = null;

const forgetConnection = (connection) => {
  if (dbPromise === connection) dbPromise = null;
};

/** Open (or reuse) the IndexedDB connection. Resolves with the `IDBDatabase`. */
export const openDatabase = () => {
  if (dbPromise) return dbPromise;

  const connection = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    // Non-destructive: creates a missing store only, so either opener yields the same schema.
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
        db.createObjectStore(STORES.MESSAGES, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
        db.createObjectStore(STORES.SESSIONS, { keyPath: 'sessionId' });
      }
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      db.onclose = () => forgetConnection(connection);
      db.onversionchange = () => {
        db.close();
        forgetConnection(connection);
      };
      resolve(db);
    };
    request.onerror = (event) => reject(event.target.error);
  });

  dbPromise = connection;
  connection.catch(() => forgetConnection(connection));
  return connection;
};

// Lets `update` tell a keyed (idempotent) put from an insert.
const KEY_PATHS = {
  [STORES.MESSAGES]: 'id',
  [STORES.SESSIONS]: 'sessionId',
};

// See .claude/context/persistence.md ("Which failures are retried").
const canRetry = (error, { idempotent }) => {
  if (error?.name === 'InvalidStateError' || error?.name === 'AbortError') return true;
  return error?.name === 'UnknownError' && idempotent;
};

// Runs `op(db)`; if the connection is dead, forgets it and runs once more when `canRetry` allows.
const doIDBOperationWithRetry = (op, { idempotent = true } = {}) => {
  const connection = openDatabase();
  return connection.then(op).catch((error) => {
    if (!canRetry(error, { idempotent })) throw error;
    forgetConnection(connection);
    return openDatabase().then(op);
  });
};

// Settles a write on its transaction: it is durable only once the transaction commits.
// `onabort` is load-bearing — see .claude/context/persistence.md ("Connection lifecycle").
/* eslint-disable no-param-reassign */
const settleOnCommit = (tx, resolve, reject, getResult) => {
  tx.oncomplete = () => resolve(getResult());
  // A request's error reaches here before the transaction aborts, so `tx.error` is still null.
  tx.onerror = (event) => reject(event.target.error ?? tx.error);
  tx.onabort = () => reject(tx.error ?? new DOMException('Transaction aborted', 'AbortError'));
};
/* eslint-enable no-param-reassign */

/** Read every record in `storeName`. Resolves with an array (never null). */
export const getAll = (storeName) => doIDBOperationWithRetry((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readonly');
  const request = tx.objectStore(storeName).getAll();
  request.onsuccess = () => resolve(request.result || []);
  request.onerror = () => reject(request.error);
}));

/** Insert a brand-new record. Rejects if a record with the same key exists. */
export const add = (storeName, record) => doIDBOperationWithRetry((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).add(record);
  settleOnCommit(tx, resolve, reject, () => record);
}), { idempotent: false });

/** Insert-or-replace a record (IndexedDB `put`). Used for updates. */
export const update = (storeName, record) => doIDBOperationWithRetry((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).put(record);
  settleOnCommit(tx, resolve, reject, () => record);
}), { idempotent: record?.[KEY_PATHS[storeName]] !== undefined });

/** Alias of `update`. */
export const put = update;

/** Read one record by key. Resolves with the record or `null` if absent. */
export const get = (storeName, key) => doIDBOperationWithRetry((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readonly');
  const request = tx.objectStore(storeName).get(key);
  request.onsuccess = () => resolve(request.result ?? null);
  request.onerror = () => reject(request.error);
}));

/** Add `record` unless a row matches it on `matchField`; resolves true if inserted. One
 * `readwrite` transaction, so it is cross-tab safe — see .claude/context/persistence.md. */
export const insertIfAbsent = (storeName, record, matchField) => doIDBOperationWithRetry(
  (db) => new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    let inserted = false;

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;

      if (!cursor) {
        store.add(record);
        inserted = true;
        return;
      }

      if (cursor.value?.[matchField] === record[matchField]) {
        // Match: stop walking, and the transaction commits with no change.
        return;
      }

      cursor.continue();
    };
    request.onerror = () => reject(request.error);

    settleOnCommit(tx, resolve, reject, () => inserted);
  }),
);

/** Delete messages. `keepInitial` (default true) keeps the `isInitialMessage` row. */
export const clearMessages = ({ keepInitial = true } = {}) => doIDBOperationWithRetry(
  (db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    const store = tx.objectStore(STORES.MESSAGES);

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      const isInitial = cursor.value?.isInitialMessage === true;
      if (!(keepInitial && isInitial)) cursor.delete();
      cursor.continue();
    };
    request.onerror = () => reject(request.error);

    settleOnCommit(tx, resolve, reject, () => true);
  }),
);

/** Startup prune: keep the first initial message, delete duplicate initials and other
 * sessions' rows, and blank the kept initial's suggested questions for b2b customers. */
export const cleanOrphans = ({ sessionId, customerType }) => doIDBOperationWithRetry(
  (db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    const store = tx.objectStore(STORES.MESSAGES);
    let initialMessageFound = false;

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;

      const value = cursor.value;
      const isDuplicateInitial = value.isInitialMessage && initialMessageFound;

      if (value.isInitialMessage && !initialMessageFound) {
        initialMessageFound = true;
        const hasSuggested = value.content?.relatedQuestions?.suggestedQuestions?.length;
        if (customerType === 'b2b' && hasSuggested) {
          cursor.update({
            ...value,
            content: {
              ...value.content,
              relatedQuestions: {
                suggestedQuestions: [],
                followUpQuestions: [],
                clarifyingQuestions: [],
              },
            },
            updatedAt: Date.now(),
          });
        }
      }

      if (value.sessionId !== sessionId || isDuplicateInitial) {
        cursor.delete();
      }
      cursor.continue();
    };
    request.onerror = () => reject(request.error);

    settleOnCommit(tx, resolve, reject, () => true);
  }),
);
